/**
 * Contrôles préalables des fichiers d'un template, exécutés DANS LE NAVIGATEUR avant tout
 * dépôt. Ils reprennent les règles du serveur (qui reste seul juge, et revérifie tout) pour
 * donner un retour immédiat : sans eux, un mauvais export Bodymovin n'était signalé qu'après
 * la création du brouillon et le dépôt complet des deux fichiers.
 */

/** Même plafond que `Template.MaxAnimationSourceSizeBytes`. */
export const MAX_ANIMATION_BYTES = 10 * 1024 * 1024;

/** Même plafond que `TemplateFilePolicy.MaxThumbnailSizeBytes`. */
export const MAX_THUMBNAIL_BYTES = 5 * 1024 * 1024;

/** Même liste blanche que `TemplateFilePolicy.ThumbnailContentTypes`. */
export const THUMBNAIL_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/avif'] as const;

/** Bornes de durée de `Template` côté domaine. */
export const MIN_DURATION_SECONDS = 3;
export const MAX_DURATION_SECONDS = 120;

export type VideoFormatValue = 0 | 1 | 2 | 3;

/** Ratios des formats de sortie, dans l'ordre de l'énumération `VideoFormat`. */
const FORMAT_RATIOS: Record<VideoFormatValue, number> = {
  0: 16 / 9,
  1: 9 / 16,
  2: 1,
  3: 21 / 9,
};

/** Même tolérance que la confirmation côté serveur (2 560 × 1 080 ≈ 21:9). */
const ASPECT_RATIO_TOLERANCE = 0.02;

export interface LottiePrecheck {
  width: number;
  height: number;
  frameRate: number;
  durationSeconds: number;
  /** Format de sortie dont le ratio correspond à l'animation, ou `null` si aucun. */
  matchingFormat: VideoFormatValue | null;
  /** Calques nommés `slot-…` : ce que la détection automatique transformera en champs. */
  slotLayerCount: number;
  warnings: string[];
}

export class TemplateFileError extends Error {}

export function formatForRatio(width: number, height: number): VideoFormatValue | null {
  const ratio = width / height;

  for (const [format, expected] of Object.entries(FORMAT_RATIOS)) {
    if (Math.abs(ratio - expected) / expected <= ASPECT_RATIO_TOLERANCE) {
      return Number(format) as VideoFormatValue;
    }
  }

  return null;
}

/** Durée de l'animation ramenée aux bornes acceptées, arrondie à la seconde. */
export function clampDuration(seconds: number): number {
  return Math.min(MAX_DURATION_SECONDS, Math.max(MIN_DURATION_SECONDS, Math.round(seconds)));
}

export function checkThumbnail(file: File): void {
  if (!(THUMBNAIL_TYPES as readonly string[]).includes(file.type)) {
    throw new TemplateFileError('La vignette doit être une image JPEG, PNG, WebP ou AVIF.');
  }

  if (file.size > MAX_THUMBNAIL_BYTES) {
    throw new TemplateFileError('La vignette dépasse 5 Mo.');
  }
}

/**
 * Lit et vérifie un export Bodymovin : structure minimale, durée, ratio, slots détectables,
 * images non embarquées (qui resteraient vides au rendu).
 */
export async function precheckLottie(file: File): Promise<LottiePrecheck> {
  if (file.size === 0 || file.size > MAX_ANIMATION_BYTES) {
    throw new TemplateFileError("Le fichier d'animation doit faire moins de 10 Mo.");
  }

  let root: unknown;

  try {
    root = JSON.parse(await file.text());
  } catch {
    throw new TemplateFileError("Le fichier d'animation n'est pas un JSON valide.");
  }

  if (!isRecord(root)) {
    throw new TemplateFileError("Le fichier d'animation n'est pas un JSON Lottie.");
  }

  const { w, h, fr, op, layers } = root;
  const ip = typeof root.ip === 'number' ? root.ip : 0;

  if (!isPositive(w) || !isPositive(h) || !isPositive(fr) || typeof op !== 'number' || !Array.isArray(layers)) {
    throw new TemplateFileError(
      "Le fichier n'a pas la structure d'une animation Lottie (w, h, fr, op, layers) : exportez-le avec Bodymovin.",
    );
  }

  const warnings: string[] = [];
  const matchingFormat = formatForRatio(w, h);

  if (matchingFormat === null) {
    warnings.push(`L'animation fait ${w}×${h} : aucun format de sortie ne correspond, le rendu sera déformé.`);
  }

  const assets = Array.isArray(root.assets) ? root.assets.filter(isRecord) : [];
  const externalImages = assets.filter(
    (asset) => typeof asset.p === 'string' && !asset.p.startsWith('data:') && !('layers' in asset),
  );

  if (externalImages.length > 0) {
    warnings.push(
      `${externalImages.length} image${externalImages.length > 1 ? 's' : ''} non incluse${externalImages.length > 1 ? 's' : ''} dans le fichier : `
        + 'hors champs image du client, elles resteront vides au rendu (option Bodymovin « Inclure les ressources dans le JSON »).',
    );
  }

  const slotLayerCount = countSlotLayers([layers, ...assets.map((asset) => asset.layers)]);

  if (slotLayerCount === 0) {
    warnings.push('Aucun calque slot-… : aucun champ ne sera détecté automatiquement, ajoutez-les à la main.');
  }

  return {
    width: w,
    height: h,
    frameRate: fr,
    durationSeconds: Math.round(((op - ip) / fr) * 100) / 100,
    matchingFormat,
    slotLayerCount,
    warnings,
  };
}

function countSlotLayers(layerLists: unknown[]): number {
  let count = 0;

  for (const list of layerLists) {
    if (!Array.isArray(list)) {
      continue;
    }

    for (const layer of list) {
      if (isRecord(layer) && typeof layer.nm === 'string' && layer.nm.startsWith('slot-')) {
        count += 1;
      }
    }
  }

  return count;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isPositive(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

/** Extension du nom de fichier, point compris (`.json`), ou chaîne vide. */
export function fileExtension(file: File): string {
  return file.name.includes('.') ? file.name.slice(file.name.lastIndexOf('.')) : '';
}
